import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserTie,
  faNewspaper,
  faBullhorn,
  faGavel,
  faUsers,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import AdminLayout from "./AdminLayout";
import { getTranslation } from "../../utils/translations";

import { useLanguage } from "../../context/LanguageContext";

const Dashboard: React.FC = () => {
  const { language } = useLanguage();

  const translations = {
    dashboard: { ar: "لوحة التحكم", fr: "Tableau de Bord" },
    ministry: {
      title: { ar: "الوزارة", fr: "Le Ministère" },
      desc: {
        ar: "إضافة وتعديل وحذف بيانات الوزراء",
        fr: "Gérer les ministres et leurs données",
      },
    },
    news: {
      title: { ar: "إدارة الأخبار", fr: "Gestion des Actualités" },
      desc: {
        ar: "إضافة وتعديل وحذف الأخبار",
        fr: "Ajouter, modifier et supprimer des actualités",
      },
    },
    announcements: {
      title: {
        ar: getTranslation("adminAnnouncementsTitle", language),
        fr: "Gestion des Annonces",
      },
      desc: {
        ar: "إضافة وتعديل وحذف الإعلانات",
        fr: "Ajouter, modifier et supprimer des annonces",
      },
    },
    legislations: {
      title: {
        ar: getTranslation("adminLegislationsTitle", language),
        fr: "Gestion des Législations",
      },
      desc: {
        ar: "إضافة وتعديل وحذف التشريعات",
        fr: "Ajouter, modifier et supprimer des législations",
      },
    },
    users: {
      title: {
        ar: getTranslation("adminUsersTitle", language),
        fr: "Gestion des Utilisateurs",
      },
      desc: {
        ar: "إضافة وتعديل وحذف المستخدمين",
        fr: "Gérer les utilisateurs et les accès",
      },
    },
    statistics: {
      title: { ar: "الإحصائيات", fr: "Statistiques" },
      desc: {
        ar: "عرض إحصائيات الموقع والزيارات",
        fr: "Voir les statistiques et visites du site",
      },
    },
  };
  return (
    <AdminLayout>
      <h1 className="text-3xl font-bold text-mauritania-green-dark mb-8">
        {language === "ar"
          ? translations.dashboard.ar
          : translations.dashboard.fr}
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Ministers Card */}
        <Link
          to="/admin/ministers"
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition duration-300"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-mauritania-green/10 text-mauritania-green mr-4">
              <FontAwesomeIcon icon={faUserTie} size="2x" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {language === "ar"
                  ? translations.ministry.title.ar
                  : translations.ministry.title.fr}
              </h2>
              <p className="text-gray-600">
                {language === "ar"
                  ? translations.ministry.desc.ar
                  : translations.ministry.desc.fr}
              </p>
            </div>
          </div>
        </Link>

        {/* News Card */}
        <Link
          to="#"
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition duration-300"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
              <FontAwesomeIcon icon={faNewspaper} size="2x" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {language === "ar"
                  ? translations.news.title.ar
                  : translations.news.title.fr}
              </h2>
              <p className="text-gray-600">
                {language === "ar"
                  ? translations.news.desc.ar
                  : translations.news.desc.fr}
              </p>
            </div>
          </div>
        </Link>

        {/* Announcements Card */}
        <Link
          to="#"
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition duration-300"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-4">
              <FontAwesomeIcon icon={faBullhorn} size="2x" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {language === "ar"
                  ? translations.announcements.title.ar
                  : translations.announcements.title.fr}
              </h2>
              <p className="text-gray-600">
                {language === "ar"
                  ? translations.announcements.desc.ar
                  : translations.announcements.desc.fr}
              </p>
            </div>
          </div>
        </Link>

        {/* Legislation Card */}
        <Link
          to="#"
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition duration-300"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
              <FontAwesomeIcon icon={faGavel} size="2x" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {language === "ar"
                  ? translations.legislations.title.ar
                  : translations.legislations.title.fr}
              </h2>
              <p className="text-gray-600">
                {language === "ar"
                  ? translations.legislations.desc.ar
                  : translations.legislations.desc.fr}
              </p>
            </div>
          </div>
        </Link>

        {/* Users Card */}
        <Link
          to="#"
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition duration-300"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
              <FontAwesomeIcon icon={faUsers} size="2x" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {language === "ar"
                  ? translations.users.title.ar
                  : translations.users.title.fr}
              </h2>
              <p className="text-gray-600">
                {language === "ar"
                  ? translations.users.desc.ar
                  : translations.users.desc.fr}
              </p>
            </div>
          </div>
        </Link>

        {/* Statistics Card */}
        <Link
          to="#"
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition duration-300"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <FontAwesomeIcon icon={faChartLine} size="2x" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {language === "ar"
                  ? translations.statistics.title.ar
                  : translations.statistics.title.fr}
              </h2>
              <p className="text-gray-600">
                {language === "ar"
                  ? translations.statistics.desc.ar
                  : translations.statistics.desc.fr}
              </p>
            </div>
          </div>
        </Link>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
